import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
	LiveLeadClientRow,
	LiveLeadConfig,
	LiveLeadDailyPoint,
	LiveLeadDelivery,
	LiveLeadKey,
	LiveLeadStats
} from '../../../models/liveLead.model';
import {
	createLiveLeadKey,
	getLiveLeadDaily,
	getLiveLeadMe,
	listLiveLeadClients,
	listLiveLeadDeliveries,
	listLiveLeadKeys,
	revokeLiveLeadKey,
	updateLiveLeadClient,
	updateLiveLeadWebhook
} from '../../../services/api/liveLead/liveLead.datric.api';
import { toastService } from '../../../services/toast.service';

export interface LiveLeadState {
	config: LiveLeadConfig | null;
	stats: LiveLeadStats | null;
	daily: LiveLeadDailyPoint[];
	deliveries: LiveLeadDelivery[];
	deliveriesTotal: number;
	deliveriesPage: number;
	keys: LiveLeadKey[];
	// Chave recém-criada em texto: existe só nesta sessão de tela.
	newKey: string | null;
	clients: LiveLeadClientRow[];
	loading: boolean;
	loadingDeliveries: boolean;
	saving: boolean;
	error: string | null;
}

const initialState: LiveLeadState = {
	config: null,
	stats: null,
	daily: [],
	deliveries: [],
	deliveriesTotal: 0,
	deliveriesPage: 1,
	keys: [],
	newKey: null,
	clients: [],
	loading: false,
	loadingDeliveries: false,
	saving: false,
	error: null
};

export const fetchLiveLeadMe = createAsyncThunk('liveLead/me', async (_, { rejectWithValue }) => {
	try {
		return await getLiveLeadMe();
	} catch (e: any) {
		return rejectWithValue(typeof e === 'string' ? e : e?.message || 'Falha ao carregar o painel');
	}
});

export const fetchLiveLeadDaily = createAsyncThunk('liveLead/daily', async (days: number | undefined) => {
	return getLiveLeadDaily(days ?? 14);
});

export const fetchLiveLeadDeliveries = createAsyncThunk(
	'liveLead/deliveries',
	async (params: { page?: number; limit?: number; status?: string }) => {
		return listLiveLeadDeliveries(params);
	}
);

export const saveLiveLeadWebhook = createAsyncThunk(
	'liveLead/saveWebhook',
	async (
		payload: {
			webhookUrl?: string | null;
			webhookHeaders?: Record<string, string> | null;
			rotateSecret?: boolean;
		},
		{ rejectWithValue }
	) => {
		try {
			const config = await updateLiveLeadWebhook(payload);
			toastService.success(payload.rotateSecret ? 'Segredo renovado' : 'Webhook salvo');
			return config;
		} catch (e: any) {
			const message = typeof e === 'string' ? e : e?.message || 'Falha ao salvar o webhook';
			toastService.error(message);
			return rejectWithValue(message);
		}
	}
);

export const fetchLiveLeadKeys = createAsyncThunk('liveLead/keys', async () => listLiveLeadKeys());

export const generateLiveLeadKey = createAsyncThunk(
	'liveLead/createKey',
	async (name: string, { dispatch }) => {
		const created = await createLiveLeadKey(name);
		dispatch(fetchLiveLeadKeys());
		return created;
	}
);

export const revokeKey = createAsyncThunk('liveLead/revokeKey', async (id: string, { dispatch }) => {
	await revokeLiveLeadKey(id);
	toastService.success('Chave revogada');
	dispatch(fetchLiveLeadKeys());
	return id;
});

export const fetchLiveLeadClients = createAsyncThunk('liveLead/clients', async () => listLiveLeadClients());

export const saveLiveLeadClient = createAsyncThunk(
	'liveLead/saveClient',
	async (
		payload: { userId: string; changes: Partial<LiveLeadClientRow> },
		{ dispatch, rejectWithValue }
	) => {
		try {
			await updateLiveLeadClient(payload.userId, payload.changes);
			toastService.success('Cliente atualizado');
			dispatch(fetchLiveLeadClients());
			return payload;
		} catch (e: any) {
			const message = typeof e === 'string' ? e : e?.message || 'Falha ao atualizar o cliente';
			toastService.error(message);
			return rejectWithValue(message);
		}
	}
);

export const liveLeadSlice = createSlice({
	name: 'liveLead',
	initialState,
	reducers: {
		// A chave em texto some da memória assim que o cliente fecha o aviso.
		clearNewKey: state => {
			state.newKey = null;
		}
	},
	extraReducers: builder => {
		builder
			.addCase(fetchLiveLeadMe.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchLiveLeadMe.fulfilled, (state, action) => {
				state.loading = false;
				state.config = action.payload.config;
				state.stats = action.payload.stats;
			})
			.addCase(fetchLiveLeadMe.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || 'Falha ao carregar o painel';
			})
			.addCase(fetchLiveLeadDaily.fulfilled, (state, action) => {
				state.daily = action.payload;
			})
			.addCase(fetchLiveLeadDeliveries.pending, state => {
				state.loadingDeliveries = true;
			})
			.addCase(fetchLiveLeadDeliveries.fulfilled, (state, action) => {
				state.loadingDeliveries = false;
				state.deliveries = action.payload.rows;
				state.deliveriesTotal = action.payload.total;
				state.deliveriesPage = action.payload.page;
			})
			.addCase(fetchLiveLeadDeliveries.rejected, state => {
				state.loadingDeliveries = false;
			})
			.addCase(saveLiveLeadWebhook.pending, state => {
				state.saving = true;
			})
			.addCase(saveLiveLeadWebhook.fulfilled, (state, action) => {
				state.saving = false;
				state.config = action.payload;
			})
			.addCase(saveLiveLeadWebhook.rejected, state => {
				state.saving = false;
			})
			.addCase(fetchLiveLeadKeys.fulfilled, (state, action) => {
				state.keys = action.payload;
			})
			.addCase(generateLiveLeadKey.fulfilled, (state, action) => {
				state.newKey = action.payload.key ?? null;
			})
			.addCase(fetchLiveLeadClients.fulfilled, (state, action) => {
				state.clients = action.payload;
			});
	}
});

export const { clearNewKey } = liveLeadSlice.actions;
